/**
 * Google Analytics 4 (GA4) Integration Utility with Consent Mode
 * 
 * This file contains helper functions for initializing and managing GA4
 * with Google Consent Mode v2 support.
 * 
 * Consent Mode allows Google tags to run in a limited way before consent,
 * enabling better measurement and conversion modeling.
 */

// Get GA4 Measurement ID from environment variable
// In Vercel, set this in Project Settings > Environment Variables
// Variable name: VITE_GA_MEASUREMENT_ID
// Value: G-XXXXXXXXXX (your GA4 Measurement ID)
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

/**
 * Check if GA4 is already initialized
 */
export function isGA4Initialized(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    typeof window.dataLayer !== 'undefined'
  );
}

/**
 * Initialize Google Analytics 4
 * 
 * The gtag.js script is already loaded in index.html.
 * This function ensures GA4 is properly configured when consent is granted.
 * 
 * Usage:
 * ```ts
 * import { initializeGA4 } from '@/lib/analytics';
 * 
 * initializeGA4();
 * ```
 */
export function initializeGA4(): void {
  if (!GA_MEASUREMENT_ID) {
    console.error('GA4 Measurement ID is not configured. Please set VITE_GA_MEASUREMENT_ID in your .env file or Vercel environment variables.');
    return;
  }

  // Ensure gtag is available (should be from index.html)
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // Update config to ensure it's active (script is already loaded in HTML)
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
    });
  } else {
    console.warn('gtag function not found. Make sure Google tag script is loaded in index.html');
  }
}

/**
 * Disable Google Analytics 4
 * 
 * Note: The script remains loaded (it's in index.html), but we clear the data layer
 * and consent is set to 'denied' via updateConsent(). This is the recommended approach
 * with Consent Mode - the script stays but doesn't collect data.
 */
export function disableGA4(): void {
  // Clear data layer to stop tracking
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer = [];
  }
}

/**
 * Track a page view
 * 
 * This function should only be called when GA4 is initialized.
 * 
 * Usage:
 * ```ts
 * import { trackPageView } from '@/lib/analytics';
 * 
 * trackPageView('/about');
 * ```
 */
export function trackPageView(path: string): void {
  if (!isGA4Initialized()) {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }
}

/**
 * Track a custom event
 * 
 * This function should only be called when GA4 is initialized.
 * 
 * Usage:
 * ```ts
 * import { trackEvent } from '@/lib/analytics';
 * 
 * trackEvent('button_click', {
 *   button_name: 'contact_form_submit',
 *   page_location: window.location.href,
 * });
 * ```
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  if (!isGA4Initialized()) {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Set default consent state (should be called before any Google tags load)
 * This is typically done in index.html, but can be called here as a fallback
 */
export function setDefaultConsent(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize data layer if not already done
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function if not already defined
  if (typeof window.gtag !== 'function') {
    window.gtag = function(...args: any[]) {
      window.dataLayer.push(args);
    };
  }

  // Set default consent to denied
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500
  });
}

/**
 * Update consent state based on user preferences
 * 
 * Usage:
 * ```ts
 * import { updateConsent } from '@/lib/analytics';
 * 
 * updateConsent({
 *   analytics: true // or false
 * });
 * ```
 */
export function updateConsent(preferences: { analytics: boolean }): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: preferences.analytics ? 'granted' : 'denied',
    ad_storage: 'denied' // We don't use advertising, so always denied
  });
}

